import type { ActionGraph, ActionNode, EventType } from "./types";

export interface ItemTemplate {
	key: string;
	templateCategory: "world" | "interact" | "loot" | "equipment";
	icon: string;
	graph: () => ActionGraph;
	itemCategory: string | null;
}

function uid(): string {
	return crypto.randomUUID();
}

const VSPACING = 150;
const HSPACING = 360;

type ActionTemplate = { id: string; type: string; [key: string]: unknown };

function addDefaultEntryPoints(graph: ActionGraph): ActionGraph {
	const nodes = { ...graph.nodes };
	const present = new Set(Object.values(nodes).map((node) => node.type));
	const defaults: EventType[] = ["OnSpawn", "OnTouch"];
	for (const [index, type] of defaults.entries()) {
		if (present.has(type)) continue;
		const id = uid();
		nodes[id] = {
			id,
			type,
			position: { x: (index + 1) * HSPACING, y: 0 },
		};
	}
	return { ...graph, nodes };
}

function linear(entry: EventType, actions: ActionTemplate[]): ActionGraph {
	const entryId = uid();
	const nodes: Record<string, ActionNode> = {
		[entryId]: { id: entryId, type: entry, position: { x: 0, y: 0 } },
	};
	const edges: ActionGraph["edges"] = [];
	let prevId = entryId;
	actions.forEach((action, i) => {
		const node: ActionNode = {
			...action,
			position: { x: 0, y: (i + 1) * VSPACING },
		} as ActionNode;
		nodes[node.id] = node;
		edges.push({
			id: uid(),
			source: prevId,
			sourceHandle: "Next",
			target: node.id,
		});
		prevId = node.id;
	});
	return addDefaultEntryPoints({ nodes, edges });
}

export const ITEM_TEMPLATES: ItemTemplate[] = [
	{
		key: "collectible",
		templateCategory: "loot",
		icon: "Package",
		graph: () =>
			linear("OnSpawn", [
				{ id: uid(), type: "Interact", label: "Ramasser", holdDuration: 0.5 },
				{ id: uid(), type: "ModifyItem", mode: "Add", itemId: "", quantity: 1 },
				{ id: uid(), type: "Despawn" },
			]),
		itemCategory: "Collectible",
	},
	{
		key: "randomLoot",
		templateCategory: "loot",
		icon: "Dice5",
		graph: () =>
			linear("OnSpawn", [
				{ id: uid(), type: "Interact", label: "Ouvrir", holdDuration: 0.5 },
				{ id: uid(), type: "AddRandomItem", entries: [] },
				{ id: uid(), type: "Despawn" },
			]),
		itemCategory: "Collectible",
	},
	{
		key: "shop",
		templateCategory: "interact",
		icon: "Store",
		graph: () =>
			linear("OnSpawn", [
				{ id: uid(), type: "Interact", label: "Parler", holdDuration: 0.5 },
				{
					id: uid(),
					type: "Dialogue",
					lines: [{ speaker: "Entity" as const, text: "Bienvenue !" }],
				},
				{
					id: uid(),
					type: "Transform",
					title: "Boutique",
					button: "Acheter",
					recipes: [],
					allowFreeMode: false,
				},
			]),
		itemCategory: null,
	},
	{
		key: "portal",
		templateCategory: "world",
		icon: "DoorOpen",
		graph: () =>
			linear("OnSpawn", [
				{ id: uid(), type: "Interact", label: "Entrer", holdDuration: 0.5 },
				{
					id: uid(),
					type: "SetAmbiance",
					preset: "Noon",
					transitionDuration: 2,
				},
				{ id: uid(), type: "Teleport", destination: "" },
			]),
		itemCategory: null,
	},
	{
		key: "heal",
		templateCategory: "interact",
		icon: "HeartPulse",
		graph: () =>
			linear("OnSpawn", [
				{ id: uid(), type: "Interact", label: "Utiliser", holdDuration: 0.5 },
				{
					id: uid(),
					type: "ModifyStat",
					mode: "Restore",
					statKey: "",
					value: "100",
					target: "player",
				},
				{
					id: uid(),
					type: "Notify",
					notifType: "Success",
					message: "Soin appliqué !",
				},
			]),
		itemCategory: null,
	},
	{
		key: "npc",
		templateCategory: "interact",
		icon: "MessageCircle",
		graph: () =>
			linear("OnSpawn", [
				{ id: uid(), type: "Interact", label: "Parler", holdDuration: 0.5 },
				{
					id: uid(),
					type: "Dialogue",
					lines: [{ speaker: "Entity" as const, text: "Bonjour !" }],
				},
			]),
		itemCategory: null,
	},
	{
		key: "conditionalNpc",
		templateCategory: "interact",
		icon: "HelpCircle",
		graph: () => {
			const entryId = uid();
			const interactId = uid();
			const branchId = uid();
			const trueDialogueId = uid();
			const rewardId = uid();
			const falseDialogueId = uid();
			const nodes: Record<string, ActionNode> = {
				[entryId]: {
					id: entryId,
					type: "OnSpawn",
					position: { x: 0, y: 0 },
				},
				[interactId]: {
					id: interactId,
					type: "Interact",
					label: "Parler",
					holdDuration: 0.5,
					position: { x: 0, y: VSPACING },
				},
				[branchId]: {
					id: branchId,
					type: "Branch",
					logicalOperator: "AND",
					conditions: [
						{
							type: "HasItem",
							operator: ">=",
							itemId: "",
							value: 1,
						},
					],
					position: { x: 0, y: VSPACING * 2 },
				},
				[trueDialogueId]: {
					id: trueDialogueId,
					type: "Dialogue",
					lines: [{ speaker: "Entity" as const, text: "Merci !" }],
					position: { x: -HSPACING / 2, y: VSPACING * 3 },
				},
				[rewardId]: {
					id: rewardId,
					type: "ModifyItem",
					mode: "Add",
					itemId: "",
					quantity: 1,
					position: { x: -HSPACING / 2, y: VSPACING * 4 },
				},
				[falseDialogueId]: {
					id: falseDialogueId,
					type: "Dialogue",
					lines: [
						{
							speaker: "Entity" as const,
							text: "Reviens quand tu auras ce qu'il faut.",
						},
					],
					position: { x: HSPACING / 2, y: VSPACING * 3 },
				},
			};
			const edges: ActionGraph["edges"] = [
				{ id: uid(), source: entryId, sourceHandle: "Next", target: interactId },
				{ id: uid(), source: interactId, sourceHandle: "Next", target: branchId },
				{ id: uid(), source: branchId, sourceHandle: "True", target: trueDialogueId },
				{ id: uid(), source: trueDialogueId, sourceHandle: "Next", target: rewardId },
				{ id: uid(), source: branchId, sourceHandle: "False", target: falseDialogueId },
			];
			return addDefaultEntryPoints({ nodes, edges });
		},
		itemCategory: null,
	},
	{
		key: "spawner",
		templateCategory: "world",
		icon: "Repeat",
		graph: () =>
			linear("OnSpawn", [
				{
					id: uid(),
					type: "Spawn",
					spawnType: "respawn",
					maxActive: 5,
					interval: 10,
					contentList: [],
				},
			]),
		itemCategory: null,
	},
	{
		key: "resource",
		templateCategory: "loot",
		icon: "Pickaxe",
		graph: () =>
			linear("OnSpawn", [
				{ id: uid(), type: "Interact", label: "Récolter", holdDuration: 1 },
				{ id: uid(), type: "ModifyItem", mode: "Add", itemId: "", quantity: 1 },
				{ id: uid(), type: "SetTimer", duration: 30 },
			]),
		itemCategory: "Collectible",
	},
	{
		key: "companion",
		templateCategory: "equipment",
		icon: "PawPrint",
		graph: () =>
			linear("OnSpawn", [{ id: uid(), type: "ToggleEquip" }]),
		itemCategory: "Companion",
	},
	{
		key: "weapon",
		templateCategory: "equipment",
		icon: "Sword",
		graph: () =>
			linear("OnSpawn", [{ id: uid(), type: "ToggleEquip" }]),
		itemCategory: "Main",
	},
	{
		key: "armor",
		templateCategory: "equipment",
		icon: "Shield",
		graph: () =>
			linear("OnSpawn", [{ id: uid(), type: "ToggleEquip" }]),
		itemCategory: "Body",
	},
	{
		key: "consumable",
		templateCategory: "equipment",
		icon: "FlaskRound",
		graph: () =>
			linear("OnConsume", [
				{
					id: uid(),
					type: "ModifyStat",
					mode: "Restore",
					statKey: "",
					value: "50",
					target: "player",
				},
				{ id: uid(), type: "ConsumeItem" },
			]),
		itemCategory: "Consumable",
	},
	{
		key: "player",
		templateCategory: "equipment",
		icon: "User",
		graph: () => {
			const onCreateId = uid();
			const onSpawnId = uid();
			const onTouchId = uid();
			const onDamageId = uid();
			const onDeathId = uid();
			const onRespawnId = uid();
			const nodes: Record<string, ActionNode> = {
				[onCreateId]: { id: onCreateId, type: "OnCreate", position: { x: 0, y: 0 } },
				[onSpawnId]: { id: onSpawnId, type: "OnSpawn", position: { x: HSPACING, y: 0 } },
				[onTouchId]: { id: onTouchId, type: "OnTouch", position: { x: HSPACING * 2, y: 0 } },
				[onDamageId]: { id: onDamageId, type: "OnDamage", position: { x: HSPACING * 3, y: 0 } },
				[onDeathId]: { id: onDeathId, type: "OnDeath", position: { x: HSPACING * 4, y: 0 } },
				[onRespawnId]: { id: onRespawnId, type: "OnRespawn", position: { x: HSPACING * 5, y: 0 } },
			};
			return { nodes, edges: [] };
		},
		itemCategory: "Player",
	},
];
